import * as Sentry from '@sentry/react'
import type { ErrorEvent, EventHint } from '@sentry/core'
import { useEffect } from 'react'
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from 'react-router-dom'
import { settings } from './settings'

const { APP_ENV, SENTRY_DSN } = settings

/**
 * Erros de domínio que representam comportamento esperado da aplicação.
 * Estes erros são filtrados no beforeSend e não são enviados ao Sentry.
 */
export const IGNORED_ERROR_NAMES = [
  'BadRequestError',
  'ConflictError',
  'ForbiddenError',
  'InvalidCredentialsError',
  'NotFoundError',
  'TrackingError',
  'UnexpectedError'
]

/**
 * Nome do erro de transporte sem resposta HTTP (ver infra/http/network-error).
 * String literal de propósito: evita acoplar `main/config` à camada `infra`.
 */
const NETWORK_ERROR_NAME = 'NetworkError'

/**
 * Mensagens de erro genéricas/externas que devem ser ignoradas.
 */
const IGNORED_ERROR_MESSAGES = [
  /O conteúdo que você está tentando acessar não está disponível/i,
  'Network Error',
  'Importing a module script failed',
  'Load failed'
]

export const isIgnoredError = (error: unknown): boolean => {
  if (error instanceof Error) {
    return IGNORED_ERROR_NAMES.includes(error.name)
  }

  if (typeof error === 'object' && error !== null && 'name' in error) {
    return IGNORED_ERROR_NAMES.includes((error as { name: string }).name)
  }

  return false
}

const handleGenerateVersion = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  const seconds = String(now.getSeconds()).padStart(2, '0')

  return `${APP_ENV}-v-${month}/${day}/${year}-${hours}:${minutes}:${seconds}`
}

export default {
  dsn: SENTRY_DSN,
  environment: APP_ENV,
  release: handleGenerateVersion(),
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    }),
    Sentry.replayIntegration()
  ],
  ignoreErrors: IGNORED_ERROR_MESSAGES,
  denyUrls: [/user-guiding.*\.js/i, /userguiding/i],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 0.1,
  beforeSend(event: ErrorEvent, hint: EventHint) {
    if (isIgnoredError(hint.originalException)) {
      return null
    }

    // NetworkError = request sem resposta HTTP (rede/CORS/proxy corporativo).
    // É mantido observável de propósito (network_verdict/correlationId/requestUrl
    // ajudam na análise de tickets), mas sempre como `info`: falha de rede do
    // cliente não é acionável pela engenharia e não deve paginar. Outage real da
    // nossa API chega como 5xx (level error) e no monitoramento de infra, não
    // aqui. Único ponto que cobre os dois caminhos de captura: o enriquecido
    // (React Query, que adiciona `network_verdict`) e o automático
    // (route-loaders/background, sem veredito).
    if ((hint.originalException as Error)?.name === NETWORK_ERROR_NAME) {
      // Dispositivo sem internet: nenhum valor de observabilidade — descarta.
      // (Para enviar também como `info`, troque por `event.level = 'info'`.)
      if (event.tags?.network_verdict === 'offline') {
        return null
      }

      event.level = 'info'
      event.tags = { ...event.tags, network_error: 'true' }
    }

    event.tags = {
      ...event.tags,
      route: window.location.pathname
    }

    event.contexts = {
      ...event.contexts,
      app: {
        url: window.location.href,
        referrer: document.referrer || undefined
      }
    }

    return event
  }
}
